import React, { useEffect, useState } from 'react';
import { Button, Row, Col } from 'reactstrap';
import { Pie, Bar } from 'react-chartjs-2';
import ExportVariantIcon from 'mdi-react/ExportVariantIcon';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';

import QuestionsStatsModel from './QuestionsStatsModel';
import QuestionType from './QuestionnaireBuilder/QuestionType';
import Api from '../../../util/api';
import Loading from '../../shared/Spinner';

import t, { partial } from '../../../util/translation/translation';

const QuestionsStatsCard = ({ eventId }) => {
  const [totalResponses, setTotalResponses] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [groupedAnswers, setGroupedAnswers] = useState({});
  const [questionNumber, setQuestionNumber] = useState(0);
  const [questionKey, setQuestionKey] = useState('');
  const [questionValues, setQuestionValues] = useState([]);
  const qS = partial('question');

  const textFieldQuestionLimit = 5;

  const groupAnswers = async () => {
    const answers = await Api.questions.fetchAnswers(eventId);

    if (answers && answers.data.length > 0) {
      setIsLoading(true);
      setTotalResponses(answers.data.length);
      const answerArr = [];
      answers.data.forEach((answerObj) => {
        answerObj?.answer?.forEach((answer) => {
          if (answer?.guest !== true) {
            answerArr.push(answer);
          }
        });
      });
      // Group the data by question
      const groupedData = answerArr.reduce((result, item) => {
        // Check if there's already an array for this question, if not, create one
        if (!result[item.question]) {
          result[item.question] = [];
        }

        // Push the current item to the array with the corresponding key
        result[item.question].push(item);

        return result;
      }, {});

      setGroupedAnswers(groupedData);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const clearAnswers = () => {
    setGroupedAnswers({});
    setTotalResponses(0);
  };

  useEffect(() => {
    groupAnswers();
    return () => {
      clearAnswers();
    };
  }, []);

  const handleCloseModal = () => {
    setQuestionValues([]);
    setQuestionKey('');
    setShowModal(false);
  };

  const renderDropDownQuestions = (key) => {
    const values = groupedAnswers[key]; // all user answers for key question
    const labels = []; // labels used in chart
    const answerArray = [];
    const count = {}; // calcualted count of each answer

    values.forEach((value) => {
      answerArray.push(value.answer); // push all user answers to one array
      value?.choices?.forEach((i) => {
        const found = labels.find((element) => element === i.choice);
        if (!found) {
          labels.push(i.choice); // push all choice to one array
        }
      });
    });

    // remove empty values and count an answers in answerArray
    answerArray
      .filter((item) => !!item)
      .forEach((a) => {
        count[a] = (count[a] || 0) + 1;
      });

    const data = []; // data used in chart (values in label order)
    labels.forEach((label) =>
      data.push(
        count[label] !== '' && count[label] !== undefined ? count[label] : 0
      )
    );

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: {
          labels: {
            boxWidth: 16,
          },
          position: 'right',
        },
      },
      maintainAspectRatio: false,
    };

    // customLabels to display lable with value
    const customLabels = labels.map(
      (label, index) => `${label}: ${data[index]}`
    );

    return (
      <div className="chart-container">
        <div className="chart-style">
          <Pie
            data={{
              datasets: [
                {
                  data,
                  backgroundColor: [
                    '#EF6C6C',
                    '#8D66F5',
                    '#2388FB',
                    '#47D78A',
                    '#F0B26A',
                  ],
                  borderWidth: 0,
                },
              ],
              labels: customLabels,
            }}
            options={chartOptions}
          />
        </div>
      </div>
    );
  };

  const renderTextFieldQuestions = (key) => {
    const values = groupedAnswers[key];
    const questionIndex = Object.keys(groupedAnswers).indexOf(key);
    return (
      <div className="container">
        <Row>
          <div className="recent-answers">
            <p>{qS('recentAnswers')}</p>
          </div>
        </Row>
        {values.map(
          (item, index) =>
            item.answer !== '' &&
            index < textFieldQuestionLimit && (
              <Row key={index}>
                <div className="answer-box">
                  <p>{item.answer}</p>
                </div>
              </Row>
            )
        )}

        {values && values.length > textFieldQuestionLimit && (
          <Row className="justify-content-center">
            <div style={{ width: 200, marginTop: 8 }}>
              <Button
                size="sm"
                color="primary"
                onClick={() => {
                  setQuestionNumber(questionIndex + 1);
                  setQuestionValues(values);
                  setQuestionKey(key);
                  setShowModal(true);
                }}
              >
                {qS('viewAllResponses')}
              </Button>
            </div>
          </Row>
        )}
      </div>
    );
  };

  const renderCheckboxQuestions = (key) => {
    const values = groupedAnswers[key]; // all user answers for key question
    const labels = []; // labels used in chart
    const answerArray = [];
    values.forEach((value) => {
      value?.answer?.forEach((answer) => answerArray.push(answer));
      value?.choices?.forEach((i) => {
        const found = labels.find((element) => element === i.choice);
        if (!found) {
          labels.push(i.choice); // push all choice to one array
        }
      });
    });

    const count = {}; // calcualted count of each answer

    // remove empty values and count an answers in answerArray
    answerArray
      .filter((item) => !!item)
      .forEach((a) => {
        count[a] = (count[a] || 0) + 1;
      });

    const data = []; // data used in chart (values in label order)
    labels.forEach((label) =>
      data.push(
        count[label] !== '' && count[label] !== undefined ? count[label] : 0
      )
    );

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: {
          labels: {
            boxWidth: 16,
          },
          position: 'right',
        },
      },
      maintainAspectRatio: false,
    };

    return (
      <div className="chart-container">
        <div className="chart-style">
          <Bar
            options={chartOptions}
            data={{
              labels,
              datasets: [
                {
                  data,
                  maxBarThickness: 16,
                  backgroundColor: '#2388FB',
                  label: '',
                },
              ],
            }}
          />
        </div>
      </div>
    );
  };

  const renderReservationQuestions = (key) => {
    const values = groupedAnswers[key];
    // Collect option labels and their capacities from the first answer entry's options
    const optionLabels = [];
    const optionCapacities = {};
    const optionOrder = [];
    const options = values[0]?.options || [];
    options.forEach((opt) => {
      optionLabels.push(opt.title);
      optionCapacities[opt.id] = opt.capacity;
      optionOrder.push(opt.id);
    });

    // Sum quantities per selected option
    const bookedById = {};
    values.forEach((value) => {
      const answer = value.answer;
      let selectedId = null;
      let qty = 1;
      if (answer && typeof answer === 'object' && answer.selectedItem) {
        selectedId = answer.selectedItem;
        qty = answer.quantity || 1;
      } else if (typeof answer === 'string' && answer !== '') {
        selectedId = answer;
      }
      if (selectedId) {
        bookedById[selectedId] = (bookedById[selectedId] || 0) + qty;
      }
    });

    const bookedData = optionOrder.map((id) => bookedById[id] || 0);
    const capacityData = optionOrder.map((id) => optionCapacities[id] || 0);

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: { labels: { boxWidth: 16 }, position: 'bottom' },
      },
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true },
      },
    };

    return (
      <div className="chart-container">
        <div className="chart-style">
          <Bar
            options={chartOptions}
            data={{
              labels: optionLabels,
              datasets: [
                {
                  label: qS('reservationBooked'),
                  data: bookedData,
                  backgroundColor: '#2388FB',
                  maxBarThickness: 24,
                },
                {
                  label: 'Available',
                  data: capacityData.map((cap, i) =>
                    Math.max(0, cap - (bookedData[i] || 0))
                  ),
                  backgroundColor: '#E8EAED',
                  maxBarThickness: 24,
                },
              ],
            }}
          />
        </div>
      </div>
    );
  };

  const handleCSVExport = async () => {
    try {
      const [answersRes, participants, companiesRes] = await Promise.all([
        Api.questions.fetchAnswers(eventId),
        Api.events.getParticipants(eventId),
        Api.companies.getCompanies(10000, 1, '', 'name', false, true, true, ''),
      ]);

      const answersData = answersRes?.data || [];

      // Build company ID → name lookup
      const companyNameMap = {};
      const companiesData = companiesRes?.data?.data || [];
      companiesData.forEach((c) => {
        companyNameMap[c.id] = c.name;
      });

      // Build userId → participant lookup
      // Participants API returns a map: { GUEST: [...], USER: [...], companyId: {...} }
      const participantsMap = participants?.data || {};
      const participantLookup = {};
      Object.keys(participantsMap).forEach((key) => {
        if (key === 'GUEST' || key === 'USER') {
          (participantsMap[key] || []).forEach((p) => {
            participantLookup[p.userId] = {
              ...p,
              companyName: companyNameMap[p.companyId] || '',
            };
          });
        } else {
          // Company participants — key is the company ID
          const companyName = companyNameMap[key] || key;
          const companyData = participantsMap[key];
          if (companyData?.participants) {
            companyData.participants.forEach((emp) => {
              participantLookup[emp.userId] = { ...emp, companyName };
            });
          }
        }
      });

      // Collect unique questions (with their options) from the first answer that has each one
      const questionMap = new Map();
      answersData.forEach((answerObj) => {
        answerObj?.answer?.forEach((a) => {
          if (a?.guest !== true && !questionMap.has(a.id)) {
            questionMap.set(a.id, a);
          }
        });
      });
      const questions = Array.from(questionMap.values());

      // Build question ID → options lookup from answer entries
      const questionOptionsMap = {};
      questions.forEach((q) => {
        questionOptionsMap[q.id] = q.options || [];
      });

      // Shared translation for headers
      const s = partial('shared');

      // CSV header
      const headers = [
        s('firstname'),
        s('lastname'),
        s('phone'),
        s('email'),
        s('company'),
        s('guests'),
      ];
      questions.forEach((q) => headers.push(q.question));

      let csv = `${headers.join(';')}\n`;

      // Rows: one per unique answer submitter
      answersData.forEach((answerObj) => {
        const participant = participantLookup[answerObj.userId];
        const userAnswers = answerObj?.answer || [];

        // Build answer map: question id → answer entry (for options access)
        const answerEntryMap = {};
        userAnswers.forEach((a) => {
          if (a?.guest !== true) {
            answerEntryMap[a.id] = a;
          }
        });

        const row = [
          String(participant?.firstname || '').replace(/;/g, ' '),
          String(participant?.lastname || '').replace(/;/g, ' '),
          String(participant?.phoneNumber || '').replace(/;/g, ' '),
          String(participant?.email || '').replace(/;/g, ' '),
          String(
            participant?.company?.name || participant?.companyName || ''
          ).replace(/;/g, ' '),
          participant?.friendAmount || 0,
        ];

        questions.forEach((q) => {
          const entry = answerEntryMap[q.id];
          let val = entry?.answer;

          if (val === undefined || val === '') {
            val = '';
          } else if (q.question_type === 'reservation') {
            // answer format: { selectedItem: "uuid", quantity: N } or plain string
            const options = questionOptionsMap[q.id] || [];
            let itemId;
            if (typeof val === 'object' && val.selectedItem) {
              itemId = val.selectedItem;
              const option = options.find((o) => o.id === itemId);
              const room = option ? option.title : itemId;
              const qty = val.quantity || 1;
              val = `${room} (×${qty})`;
            } else if (typeof val === 'string') {
              const option = options.find((o) => o.id === val);
              val = option ? option.title : val;
            } else {
              val = JSON.stringify(val);
            }
          } else if (Array.isArray(val)) {
            // checkbox answer: ['choice1', 'choice2']
            val = val.join(', ');
          } else if (typeof val === 'object') {
            val = JSON.stringify(val);
          }
          row.push(String(val).replace(/;/g, ' ').replace(/\n/g, ' '));
        });

        csv += `${row.join(';')}\n`;
      });

      fileDownload(`﻿${csv}`, `event-questions-${eventId}.csv`);
    } catch (e) {
      console.error(e);
      toast.error(t('shared.somethingWentWrong'));
    }
  };
  // Quetions number variable
  let itemID = 0;
  return (
    <div>
      <Row className="row align-center">
        <Col className="col-10">
          <p className="total-responses">
            {qS('totalResponses')} : <b>{totalResponses}</b>
          </p>
        </Col>
        <Col className="col-2">
          <Button onClick={handleCSVExport} color="secondary" size="sm" outline>
            <ExportVariantIcon
              style={{
                height: '16px',
                marginTop: -2,
              }}
            />
            {qS('exportCSV')}
          </Button>
        </Col>
      </Row>
      {isLoading ? (
        <Loading />
      ) : (
        Object.entries(groupedAnswers).map(([key, value]) => {
          itemID += 1;

          const answerLength = value.filter((item) => item.answer !== '');

          return (
            <div
              key={`question-${key}`}
              className="summary-question-card card-margin"
            >
              <div className="content-area">
                <Row>
                  <Col className="question-header">
                    <div className="question-id">
                      <p>{itemID}</p>
                    </div>
                    <div className="question-description">
                      <h5>{key}</h5>
                      <p>
                        {answerLength.length} {qS('answers')} {value.length}{' '}
                        {qS('users')}
                      </p>
                    </div>
                  </Col>
                  <Col className="col-3 d-flex justify-content-end">
                    <QuestionType type={value[0].question_type} />
                  </Col>
                </Row>
              </div>
              <hr />
              <div className="content-area">
                <div className="question-content">
                  {value[0].question_type === 'text_field' &&
                    renderTextFieldQuestions(key)}

                  {value[0].question_type === 'dropdown' &&
                    renderDropDownQuestions(key)}

                  {value[0].question_type === 'radio_button' &&
                    renderDropDownQuestions(key)}

                  {value[0].question_type === 'reservation' &&
                    renderReservationQuestions(key)}

                  {value[0].question_type === 'checkbox' &&
                    renderCheckboxQuestions(key)}
                </div>
              </div>
            </div>
          );
        })
      )}
      <QuestionsStatsModel
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        questionNumber={questionNumber}
        questionKey={questionKey}
        questionValues={questionValues}
      />
    </div>
  );
};

export default QuestionsStatsCard;
